import Link from '@mui/material/Link';
import { Box, Stack, Avatar } from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { useAppSelector } from 'src/hook/hooks';
import { selectUserPresenceMap } from 'src/redux/slices/userPresenceSlice';
import { UserPresenceAvatarWrap } from 'src/pages/chat/components/common/avatar';
import { usePremissionContext } from 'src/pages/auth/hooks/use-permissions-context';

// ----------------------------------------------------------------------

type Props = {
  row: any;
  headCellId: string;
  isSuperAdmin: boolean;
  editHref: (id: string) => string;
  tenantUserDetailsHref?: (tenantId: string, id: string) => string;
  currentTenantId?: string;
  handleEditContact?: (id: string) => string | Promise<void>;
  /** When `USER`, show live presence dot on avatar (global Redux + socket). */
  moduleName?: string;
};

// ----------------------------------------------------------------------

export function RowCellUserProfile({
  row,
  headCellId,
  isSuperAdmin,
  editHref,
  tenantUserDetailsHref,
  currentTenantId,
  handleEditContact,
  moduleName,
}: Props) {
  const presenceMap = useAppSelector(selectUserPresenceMap);
  const { hasPermission } = usePremissionContext();
  const canViewUserPresence = hasPermission('userprofile');
  const firstChar =
    'name' in row
      ? row.name
      : 'firstName' in row
        ? row.firstName || row.lastName
        : 'teamName' in row
          ? row.teamName
          : undefined;

  const rowData = row as any;
  const showAvatar = 'ProfileURL' in row && headCellId !== 'email';
  const profileUrl = 'ProfileURL' in row ? row.ProfileURL : undefined;

  const isUserModule = moduleName?.toLowerCase() === 'user';
  const userId = 'id' in row ? String(row.id) : '';
  const showPresenceDot =
    canViewUserPresence && isUserModule && !!userId && headCellId !== 'email' && showAvatar;
  const isOnline = !!(userId && presenceMap[userId]);

  const isEmailField = headCellId === 'email';

  const displayName = isEmailField
    ? row.email || ''
    : rowData?.firstName || rowData?.lastName
      ? `${rowData?.firstName || ''} ${rowData?.lastName || ''}`
      : 'email' in row
        ? row.email
        : '';

  const subText = isEmailField
    ? ''
    : rowData?.firstName || rowData?.lastName
      ? 'email' in row
        ? row.email
        : ''
      : '';

  const getHref = () => {
    if (!('id' in row)) return undefined;

    if (tenantUserDetailsHref && currentTenantId) {
      return tenantUserDetailsHref(currentTenantId, row.id);
    }

    const href = editHref(row.id);
    if (!href || href === '#') return undefined;

    return href;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (handleEditContact && 'id' in row) {
      const href = getHref();
      if (!href) {
        e.preventDefault();
        handleEditContact(row.id);
      }
    }
  };

  return (
    <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
      {showAvatar && (
        <UserPresenceAvatarWrap showPresence={!!showPresenceDot} isOnline={isOnline}>
          <Avatar
            alt={firstChar}
            src={profileUrl?.startsWith('http') ? profileUrl : undefined}
            sx={{
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              },
            }}
          >
            {!(profileUrl?.startsWith('http')) && firstChar?.charAt(0).toUpperCase()}
          </Avatar>
        </UserPresenceAvatarWrap>
      )}

      <Stack sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}>
        <Link
          {...(getHref() ? { component: RouterLink, href: getHref() } : { component: 'span' })}
          onClick={handleClick}
          color="inherit"
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            fontWeight: 600,
            textDecoration: 'none',
            '&:hover': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
          }}
        >
          {displayName}
        </Link>
        {/* <Box
          component="span"
          sx={{
            color: 'text.disabled',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {subText}
        </Box> */}
      </Stack>
    </Box>
  );
}
